import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import {
  SHIPMENT_METRICS_QUERY,
  ACTIVE_SHIPMENTS_QUERY,
  COMPLETED_SHIPMENTS_QUERY,
  ACTIVE_SHIPMENT_DETAILS_CLIENT_QUERY,
  COMPLETED_SHIPMENT_DETAILS_CLIENT_QUERY,
  ACTIVE_SHIPMENT_DETAILS_SELLER_QUERY,
  COMPLETED_SHIPMENT_DETAILS_SELLER_QUERY,
  ACTIVE_SHIPMENT_DETAILS_NOTIFICADO_QUERY,
  COMPLETED_SHIPMENT_DETAILS_NOTIFICADO_QUERY
} from '../../services/graphql/queries.js';
import { 
  Search, 
  Filter, 
  Package, 
  Truck, 
  CheckCircle,
  Clock,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ExternalLink,
  Plus,
  FileText
} from 'lucide-react';
import SearchBar from './SearchBar.jsx';
import TabsSystem from './TabsSystem.jsx';
import UnapprovedEventsTable from './UnapprovedEventsTable.jsx';
import ShipmentEventsModal from './ShipmentEventsModal.jsx';
import BLsModal from './BLsModal.jsx';


const ShipmentsTable = ({ userRole, userId }) => {
  const [activeTab, setActiveTab] = useState('in_transit'); // 'in_transit', 'completed', or 'pending'
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [isBLsModalOpen, setIsBLsModalOpen] = useState(false);

  // Function to get the appropriate query based on role and tab
  const getQueryByRole = useCallback((role, activeTab) => {
    if (role === 'Cliente') {
      return activeTab === 'in_transit'
        ? ACTIVE_SHIPMENT_DETAILS_CLIENT_QUERY
        : COMPLETED_SHIPMENT_DETAILS_CLIENT_QUERY;
    } else if (role === 'Vendedor') {
      return activeTab === 'in_transit'
        ? ACTIVE_SHIPMENT_DETAILS_SELLER_QUERY
        : COMPLETED_SHIPMENT_DETAILS_SELLER_QUERY;
    } else if (role === 'Notificado') {
      return activeTab === 'in_transit'
        ? ACTIVE_SHIPMENT_DETAILS_NOTIFICADO_QUERY
        : COMPLETED_SHIPMENT_DETAILS_NOTIFICADO_QUERY;
    }
    // Administrador/Transportista use original queries
    return activeTab === 'in_transit'
      ? ACTIVE_SHIPMENTS_QUERY
      : COMPLETED_SHIPMENTS_QUERY;
  }, []);

  // Modal handlers
  const handleViewShipment = (shipment) => {
    setSelectedShipment(shipment);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedShipment(null);
  };

  const handleViewBLs = (manifest) => {
    setSelectedManifest(manifest);
    setIsBLsModalOpen(true);
  };

  const handleCloseBLsModal = () => {
    setIsBLsModalOpen(false);
    setSelectedManifest(null);
  };

  // Get metrics data for tab counts
  const { data: metricsData } = useQuery(SHIPMENT_METRICS_QUERY, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-first'
  });

  // Get shipments data based on active tab and user role
  const { data: shipmentsData, loading, error, refetch } = useQuery(
    getQueryByRole(userRole, activeTab),
    {
      errorPolicy: 'all',
      fetchPolicy: 'cache-and-network',
      skip: activeTab === 'pending' || !userRole
    }
  );



  // Obtener columnas según el rol (Dashboard.md requirements) - memoizado
  const getColumnsByRole = useCallback((role, activeTab) => {
    // Define base columns for Cliente/Vendedor/Notificado
    let baseColumns = ['billOfLading', 'commercialInvoice', 'consignee', 'supplier', 'in_transit', 'created_at', 'delivered_at'];

    // Define columns for Admin/Transportista
    let adminColumns = ['manifest', 'reference', 'origin_name', 'destination_name', 'full_name', 'in_transit', 'created_at', 'delivered_at'];

    // Remove 'delivered_at' from "En tránsito" view (only show in "Completados")
    if (activeTab === 'in_transit') {
      baseColumns = baseColumns.filter(col => col !== 'delivered_at');
      adminColumns = adminColumns.filter(col => col !== 'delivered_at');
    }

    if (role === 'Administrador' || role === 'Transportista') {
      return adminColumns;
    }

    return baseColumns; // Cliente, Vendedor y Notificado
  }, []);

  // Get real data from backend or fallback to mock data
  const shipmentsFromBackend = useMemo(() => {
    if (activeTab === 'pending') return [];

    // Different query keys based on role
    let queryKey;
    if (userRole === 'Cliente') {
      queryKey = activeTab === 'in_transit' ? 'activeShipmentDetailsForClient' : 'completedShipmentDetailsForClient';
    } else if (userRole === 'Vendedor') {
      queryKey = activeTab === 'in_transit' ? 'activeShipmentDetailsForSeller' : 'completedShipmentDetailsForSeller';
    } else if (userRole === 'Notificado') {
      queryKey = activeTab === 'in_transit' ? 'activeShipmentDetailsForNotificado' : 'completedShipmentDetailsForNotificado';
    } else {
      // Administrador/Transportista
      queryKey = activeTab === 'in_transit' ? 'activeShipments' : 'completedShipments';
    }

    const data = shipmentsData?.[queryKey] || [];

    return data;
  }, [shipmentsData, activeTab, userRole]);

  // Convert backend data format to match component expectations
  const normalizedShipments = useMemo(() => {
    return shipmentsFromBackend.map(item => {
      // Check if this is ShipmentDetailsView (Cliente/Vendedor/Notificado) or regular Shipment (Admin/Transportista)
      const isShipmentDetailsView = userRole === 'Cliente' || userRole === 'Vendedor' || userRole === 'Notificado';
      
      if (isShipmentDetailsView) {
        // For ShipmentDetailsView, the item IS the detail with shipment nested
        return {
          manifest: item.manifest,
          reference: item.reference,
          inTransit: item.in_transit,
          createdAt: item.created_at,
          deliveredAt: item.delivered_at,
          loggedUser: item.shipment?.loggedUser ? {
            id: item.shipment.loggedUser.id,
            fullName: item.shipment.loggedUser.full_name
          } : null,
          shipmentDetails: {
            billOfLading: item.bill_of_lading || 'N/A',
            commercialInvoice: item.commercial_invoice || 'N/A',
            consignee: {
              id: item.consignee?.id || '',
              fullName: item.consignee?.full_name || item.additional_consignees || 'N/A'
            },
            supplier: {
              id: item.suppliers?.[0]?.id || '',
              supplierName: item.suppliers?.length > 0
                ? item.suppliers.map(s => s.supplier_name).join(', ')
                : 'N/A'
            },
          },
          origin: { originName: item.shipment?.origin?.origin_name || 'N/A' },
          destination: { destinationName: item.shipment?.destination?.destination_name || 'N/A' },
          events: item.shipment?.events || []
        };
      } else {
        // For regular Shipment (Admin/Transportista), use original logic
        const firstDetail = item.details && item.details.length > 0 ? item.details[0] : null;
        
        return {
          manifest: item.manifest,
          reference: item.reference,
          inTransit: item.in_transit,
          createdAt: item.created_at,
          deliveredAt: item.delivered_at,
          notes: item.notes,
          loggedUser: item.loggedUser ? {
            id: item.loggedUser.id,
            fullName: item.loggedUser.full_name
          } : null,
          shipmentDetails: {
            billOfLading: firstDetail?.bill_of_lading || 'N/A',
            commercialInvoice: firstDetail?.commercial_invoice || 'N/A',
            consignee: {
              id: firstDetail?.consignee?.id || '',
              fullName: firstDetail?.consignee?.full_name || firstDetail?.additional_consignees || 'N/A'
            },
            supplier: {
              id: firstDetail?.suppliers?.[0]?.id || '',
              supplierName: firstDetail?.suppliers?.length > 0
                ? firstDetail.suppliers.map(s => s.supplier_name).join(', ')
                : 'N/A'
            },
          },
          origin: { originName: item.origin?.origin_name || 'N/A' },
          destination: { destinationName: item.destination?.destination_name || 'N/A' },
          events: item.events || []
        };
      }
    });
  }, [shipmentsFromBackend, userRole]);

  const shipmentsToUse = normalizedShipments;

  // Aplicar búsqueda en tiempo real - memoizado para evitar cálculos innecesarios
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) {
      return shipmentsToUse;
    }

    return shipmentsToUse.filter(shipment => {
      const searchLower = searchTerm.toLowerCase();
      
      // Campos de búsqueda según rol (Dashboard.md requirements)
      if (userRole === 'Administrador' || userRole === 'Transportista') {
        return (
          shipment.manifest?.toLowerCase().includes(searchLower) ||
          shipment.reference?.toLowerCase().includes(searchLower) ||
          shipment.shipmentDetails.billOfLading?.toLowerCase().includes(searchLower)
        );
      } else {
        // Cliente y Notificado: solo bill_of_lading
        return shipment.shipmentDetails.billOfLading?.toLowerCase().includes(searchLower);
      }
    });
  }, [searchTerm, shipmentsToUse, userRole]);

  const columns = getColumnsByRole(userRole, activeTab);
  
  // Build tabs based on user role with real metrics data
  const tabs = [
    { 
      id: 'in_transit', 
      label: 'En Tránsito', 
      icon: Clock,
      count: metricsData?.shipmentMetrics?.inTransit || 0
    },
    { 
      id: 'completed', 
      label: 'Completados', 
      icon: CheckCircle,
      count: metricsData?.shipmentMetrics?.completed || 0
    }
  ];

  // Add Estados Pendientes tab only for Admin and Transportista
  if (userRole === 'Administrador' || userRole === 'Transportista') {
    tabs.splice(1, 0, { // Insert at index 1 (between in_transit and completed)
      id: 'pending', 
      label: 'Estados Pendientes', 
      icon: AlertTriangle,
      count: metricsData?.shipmentMetrics?.unapprovedEvents || 0
    });
  }

  const getColumnLabel = (column) => {
    const labels = {
      manifest: 'Manifiesto',
      reference: 'Referencia',
      origin_name: 'Origen',
      destination_name: 'Destino',
      full_name: 'Usuario que registró',
      in_transit: 'Estado',
      created_at: 'Fecha Creación',
      delivered_at: 'Fecha Entrega',
      billOfLading: 'Conocimiento de Embarque',
      commercialInvoice: 'Factura Comercial',
      consignee: 'Consignatario',
      supplier: 'Proveedor',
    };
    return labels[column] || column;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderCellValue = (shipment, column) => {
    switch (column) {
      case 'manifest':
        return (
          <div className="flex items-center space-x-2">
            <Package className="h-4 w-4 text-gray-400" />
            <span className="font-medium text-gray-900">{shipment.manifest}</span>
          </div>
        );

      case 'reference':
        return (
          <div className="flex items-center space-x-2">
            <Truck className="h-4 w-4 text-gray-400" />
            <span className="font-medium text-gray-900">{shipment.reference}</span>
          </div>
        );
        
      case 'origin_name':
        return shipment.origin?.originName || 'N/A';
        
      case 'destination_name':
        return shipment.destination?.destinationName || 'N/A';
        
      case 'full_name':
        return shipment.loggedUser?.fullName || 'N/A';
        
      case 'in_transit':
        return (
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            shipment.inTransit
              ? 'bg-yellow-100 text-yellow-800'
              : 'bg-green-100 text-green-800'
          }`}>
            {shipment.inTransit ? 'En Tránsito' : 'Completado'}
          </span>
        );
        
      case 'created_at':
        return formatDate(shipment.createdAt);
        
      case 'delivered_at':
        return formatDate(shipment.deliveredAt);
        
      case 'billOfLading':
        return shipment.shipmentDetails.billOfLading;
        
      case 'commercialInvoice':
        return shipment.shipmentDetails.commercialInvoice;
        
      case 'consignee':
        return shipment.shipmentDetails.consignee?.fullName || 'N/A';

      case 'supplier':
        return shipment.shipmentDetails.supplier?.supplierName || 'N/A';

      default:
        return 'N/A';
    }
  };

  if (!userRole) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-500">No se pudo determinar el rol del usuario</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full max-w-full overflow-hidden">
      {/* Tabs */}
      <TabsSystem 
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Search Bar - Only show for shipments tabs */}
      {activeTab !== 'pending' && (
        <SearchBar 
          userRole={userRole}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />
      )}

      {/* Content Area */}
      {activeTab === 'pending' ? (
        <UnapprovedEventsTable 
          userRole={userRole} 
          user={{ id: userId, role: { role_name: userRole } }}
        />
      ) : loading ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="text-center py-12">
            <div className="h-12 w-12 bg-gray-300 rounded-full animate-pulse mx-auto mb-4"></div>
            <p className="text-gray-500 text-lg">Cargando envíos...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="text-center py-12">
            <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <p className="text-red-500 text-lg">Error al cargar envíos</p>
            <p className="text-gray-400 text-sm mt-1">{error.message}</p>
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {filteredData.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No hay envíos disponibles</p>
            <p className="text-gray-400 text-sm mt-1">
              {searchTerm ? 'Intenta ajustar tu búsqueda' : `No se encontraron envíos ${activeTab === 'in_transit' ? 'en tránsito' : 'completados'}`}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                  {columns.map((column) => (
                    <th
                      key={column}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      {getColumnLabel(column)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((shipment, index) => (
                  <tr key={shipment.manifest || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        <button 
                          onClick={() => handleViewShipment(shipment)}
                          className="text-primary-600 hover:text-primary-900 flex items-center space-x-1 transition-colors"
                        >
                          <ExternalLink className="h-4 w-4" />
                          <span>Estados</span>
                        </button>
                        {(userRole === 'Administrador' || userRole === 'Transportista') && (
                          <button 
                            onClick={() => handleViewBLs(shipment.manifest)}
                            className="text-blue-600 hover:text-blue-900 flex items-center space-x-1 transition-colors"
                          >
                            <FileText className="h-4 w-4" />
                            <span>BLs</span>
                          </button>
                        )}
                      </div>
                    </td>
                    {columns.map((column) => (
                      <td key={column} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {renderCellValue(shipment, column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      )}

      {/* Summary - Only show for shipments tabs */}
      {activeTab !== 'pending' && filteredData.length > 0 && (
        <div className="text-sm text-gray-500 text-center">
          Mostrando {filteredData.length} envío{filteredData.length !== 1 ? 's' : ''} 
          {searchTerm && ` para "${searchTerm}"`}
        </div>
      )}

      {/* Shipment Events Modal */}
      <ShipmentEventsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        shipment={selectedShipment}
        userRole={userRole}
      />

      {/* BLs Modal */}
      <BLsModal
        isOpen={isBLsModalOpen}
        onClose={handleCloseBLsModal}
        manifest={selectedManifest}
        userRole={userRole}
      />

    </div>
  );
};

export default ShipmentsTable;